import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import {
  getStudentAnalyticsTrades,
  getStudentAccounts,
  getStudentSessions,
  getStudentRoutines,
  getStudentGoals,
  getStudentAccountGroups,
  getStudentStrategies,
  getMyStudents,
} from "wasp/client/operations";
import StudentLayout from "./StudentLayout";
import DashboardView from "../dashboard/DashboardView";
import useTranslation from "../client/hooks/useTranslation";
import { trackStudentViewed } from "../client/utils/posthog";

export default function StudentDashboardPage() {
  const t = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();

  const { data: students = [], isLoading: loadingStudents } = useQuery(getMyStudents);
  const student = studentId ? students.find((s: any) => s.studentId === studentId) : undefined;
  const permissions = student?.permissions ?? {
    canViewJournal: false,
    canViewDashboard: false,
    canViewAnalytics: false,
    canViewSessions: false,
    canViewPropFirmManager: false,
  };

  const enabled = !!studentId && !loadingStudents && permissions.canViewDashboard;

  useEffect(() => {
    if (studentId && !loadingStudents && permissions.canViewDashboard) {
      trackStudentViewed(studentId);
    }
  }, [studentId, loadingStudents, permissions.canViewDashboard]);

  const { data: trades = [] } = useQuery(
    getStudentAnalyticsTrades,
    { studentId: studentId ?? "", excludeInactive: false },
    { enabled: enabled && permissions.canViewAnalytics },
  );
  const { data: accounts = [] } = useQuery(
    getStudentAccounts,
    { studentId: studentId ?? "" },
    { enabled: enabled && permissions.canViewPropFirmManager },
  );
  const { data: sessions = [] } = useQuery(
    getStudentSessions,
    { studentId: studentId ?? "" },
    { enabled: enabled && permissions.canViewSessions },
  );
  const { data: routines = [] } = useQuery(
    getStudentRoutines,
    { studentId: studentId ?? "" },
    { enabled },
  );
  const { data: goals = [] } = useQuery(
    getStudentGoals,
    { studentId: studentId ?? "" },
    { enabled },
  );
  const { data: accountGroups = [] } = useQuery(
    getStudentAccountGroups,
    { studentId: studentId ?? "" },
    { enabled: enabled && permissions.canViewAnalytics },
  );
  const { data: strategies = [] } = useQuery(
    getStudentStrategies,
    { studentId: studentId ?? "" },
    { enabled: enabled && permissions.canViewAnalytics },
  );

  if (!studentId) return null;

  if (loadingStudents) {
    return (
      <StudentLayout
        studentId={studentId}
        studentFirstName={null}
        studentLastName={null}
        permissions={permissions}
      >
        <p className="text-sm text-muted-foreground">Chargement...</p>
      </StudentLayout>
    );
  }

  if (!permissions.canViewDashboard) {
    return (
      <StudentLayout
        studentId={studentId}
        studentFirstName={student?.student?.firstName ?? null}
        studentLastName={student?.student?.lastName ?? null}
        permissions={permissions}
      >
        <p className="text-muted-foreground text-sm">{t.mentor.noPermissionSection}</p>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout
      studentId={studentId}
      studentFirstName={student?.student?.firstName ?? null}
      studentLastName={student?.student?.lastName ?? null}
      studentEmail={student?.student?.email ?? undefined}
      permissions={permissions}
    >
      <DashboardView
        trades={trades as any[]}
        accounts={accounts as any[]}
        sessions={sessions as any[]}
        routines={routines as any[]}
        goals={goals as any[]}
        accountGroups={accountGroups as any[]}
        strategies={strategies as any[]}
        readOnly={true}
      />
    </StudentLayout>
  );
}
